// Progressive enhancement for featured_collection_block.style = auto only.
const CACHE_KEY = 'real-promo-colors-v1';
const MAX_COLORS = 100;
const colors = new Map();
const watchedImages = new WeakSet();
let storedColors;

function imageKey(image) {
  const url = new URL(image.currentSrc || image.src, document.baseURI);
  // Keep the image identity and Shopify version, independent of responsive size.
  url.searchParams.delete('width');
  url.searchParams.delete('height');
  return url.href;
}

function validColor(value) {
  return Array.isArray(value) && value.length === 3 && value.every((n) => Number.isInteger(n) && n >= 209 && n <= 255);
}

function readColor(key) {
  if (!storedColors) {
    try {
      const saved = JSON.parse(localStorage.getItem(CACHE_KEY) || '[]');
      storedColors = new Map(Array.isArray(saved) ? saved.slice(-MAX_COLORS) : []);
    } catch {
      storedColors = new Map();
    }
  }
  const value = storedColors.get(key);
  return validColor(value) ? value : null;
}

function saveColor(key, color) {
  readColor(key);
  storedColors.delete(key);
  storedColors.set(key, color);
  while (storedColors.size > MAX_COLORS) storedColors.delete(storedColors.keys().next().value);
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify([...storedColors]));
  } catch {
    // Storage restrictions must not affect the disclosure or the image.
  }
}

function sampleColor(pixels) {
  const buckets = new Map();
  for (let i = 0; i < pixels.length; i += 4) {
    const [r, g, b, alpha] = pixels.subarray(i, i + 4);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    // Avoid letterboxing, transparent cutout backgrounds, white labels and black type.
    if (alpha < 128 || min > 235 || max < 25) continue;
    const weight = (alpha / 255) * (1 + (max - min) / 128);
    const key = `${r >> 5},${g >> 5},${b >> 5}`;
    const bucket = buckets.get(key) || { weight: 0, rgb: [0, 0, 0] };
    bucket.weight += weight;
    [r, g, b].forEach((value, channel) => (bucket.rgb[channel] += value * weight));
    buckets.set(key, bucket);
  }
  const dominant = [...buckets.values()].sort((a, b) => b.weight - a.weight)[0];
  if (!dominant) return null;
  // 18% source / 82% white bounds every channel at 209 or above.
  return dominant.rgb.map((value) => Math.round(255 * 0.82 + (value / dominant.weight) * 0.18));
}

function analyzeImage(image, key) {
  if (!colors.has(key)) {
    const cached = readColor(key);
    colors.set(key, cached ? Promise.resolve(cached) : new Promise((resolve) => {
      const analyze = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 32;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, 32, 32);
          const color = sampleColor(context.getImageData(0, 0, 32, 32).data);
          if (color) saveColor(key, color);
          resolve(color);
        } catch {
          // A third-party cutout may disallow canvas access. Keep the CSS fallback.
          resolve(null);
        }
      };
      if ('requestIdleCallback' in window) window.requestIdleCallback(analyze, { timeout: 1000 });
      else window.setTimeout(analyze, 0);
    }));
  }
  return colors.get(key);
}

function initializeAutoColors() {
  const groups = new Map();
  document.querySelectorAll('[data-real-auto-color]').forEach((card) => {
    const id = card.dataset.realAutoColor;
    if (!groups.has(id)) groups.set(id, []);
    groups.get(id).push(card);
  });

  groups.forEach((cards) => {
    // Use the compact artwork for every occurrence of this promotion on the page.
    // Without a compact trigger (e.g. page 2), use the first expanded item image.
    const source = cards.find((card) => card.matches('[data-feature-highlight-toggle]')) || cards[0];
    const image = source.querySelector('img');
    const reveal = (color) => {
      document.querySelectorAll('[data-real-auto-color]').forEach((card) => {
        if (card.dataset.realAutoColor !== source.dataset.realAutoColor) return;
        if (color) {
          card.style.setProperty('--feature-auto-background', `rgb(${color.join(' ')})`);
          // Reconstruct the source hue from the cached pale tint, then shade it with ink.
          const media = color.map((channel) => Math.round(Math.max(0, (channel - 255 * .82) / .18) * .55 + 48 * .45));
          card.style.setProperty('--feature-auto-media-background', `rgb(${media.join(' ')})`);
        }
        card.dataset.autoColorState = 'ready';
      });
    };
    if (!image) { reveal(null); return; }

    // A cache hit paints before image decoding, avoiding a skeleton on repeat visits.
    const cached = readColor(imageKey(image));
    if (cached) reveal(cached);

    const apply = async () => {
      if (!image.naturalWidth) { reveal(null); return; }
      const key = imageKey(image);
      const color = await analyzeImage(image, key);
      if (!image.isConnected || imageKey(image) !== key) return;
      reveal(color);
    };
    if (!watchedImages.has(image)) {
      watchedImages.add(image);
      image.addEventListener('load', apply);
      image.addEventListener('error', () => reveal(null));
    }
    if (image.complete) apply();
  });
}

initializeAutoColors();
document.addEventListener('shopify:section:load', initializeAutoColors);
